import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LocaleProvider } from "@/contexts/LocaleContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import OrgScopedRoute from "@/components/OrgScopedRoute";
import SwUpdater from "@/components/SwUpdater";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AcceptInvitation from "@/pages/AcceptInvitation";
import Dashboard from "@/pages/Dashboard";
import OnboardingWizard from "@/pages/OnboardingWizard";
import Sites from "@/pages/Sites";
import Users from "@/pages/Users";
import Billing from "@/pages/Billing";
import OrgSettings from "@/pages/OrgSettings";
import AuditLog from "@/pages/AuditLog";
import NotificationsLog from "@/pages/NotificationsLog";
import SuperAdmin from "@/pages/SuperAdmin";
import SuperAdminIntegrations from "@/pages/SuperAdminIntegrations";
import HostHome from "@/pages/HostHome";

import SettingsBranding from "@/pages/SettingsBranding";
import SettingsVisitorTypes from "@/pages/SettingsVisitorTypes";
import SettingsConsentDocs from "@/pages/SettingsConsentDocs";
import SettingsBadgeTemplates from "@/pages/SettingsBadgeTemplates";
import SettingsIntegrations from "@/pages/SettingsIntegrations";
import SettingsApiTokens from "@/pages/SettingsApiTokens"; // v2 PR-2B
// v2 PR-2B — Swagger UI for the public API is served directly from the backend
// at /api-docs (Kubernetes ingress sends anything matching /api* to the API
// pod), so we don't have a frontend route for it. The Settings → API tokens
// page links to it as an external anchor.
// v1.2 PR-C — consolidated settings pages (replace 6 legacy pages with 3 tabs)
import SettingsNotifications from "@/pages/SettingsNotifications";
import SettingsContractorCompliance from "@/pages/SettingsContractorCompliance";
import SettingsGdprPrivacy from "@/pages/SettingsGdprPrivacy";

import PreRegistrations from "@/pages/PreRegistrations";
import PreRegistrationNew from "@/pages/PreRegistrationNew";
import Notifications from "@/pages/Notifications";
import VisitorPass from "@/pages/VisitorPass";

import LiveDashboard from "@/pages/LiveDashboard";
import Evacuation from "@/pages/Evacuation";
import EvacuationActive from "@/pages/EvacuationActive";
import EvacuationWarden from "@/pages/EvacuationWarden";
import { EvacuationHistory, EvacuationHistoryDetail } from "@/pages/EvacuationHistory";
import Reports from "@/pages/Reports";

import Contractors from "@/pages/Contractors";
import ContractorCompanyDetail from "@/pages/ContractorCompanyDetail";
import PermitsToWork from "@/pages/PermitsToWork";
import ContractorOverrides from "@/pages/ContractorOverrides";
import SettingsWatchlist from "@/pages/SettingsWatchlist";
import { WatchlistHits, WatchlistHitDetail } from "@/pages/WatchlistHits";

import HostMobileDashboard from "@/pages/HostMobileDashboard";
import ReceptionMobile from "@/pages/ReceptionMobile";

// v1.2 — new IA pages
import Kiosks from "@/pages/Kiosks";
import AccountProfile from "@/pages/AccountProfile";
import SuperAdminOverview from "@/pages/SuperAdminOverview";
import SuperAdminProvision from "@/pages/SuperAdminProvision";
import SuperAdminAuditLog from "@/pages/SuperAdminAuditLog";
import SuperAdminPrivacyAcceptances from "@/pages/SuperAdminPrivacyAcceptances";
import SuperAdminNotificationsLogCrossOrg from "@/pages/SuperAdminNotificationsLogCrossOrg";
import SuperAdminMaintenance from "@/pages/SuperAdminMaintenance";
import SuperAdminBuild from "@/pages/SuperAdminBuild";

// Phase B3 — Help (KB + Tickets).
import HelpKb from "@/pages/HelpKb";
import HelpKbCategory from "@/pages/HelpKbCategory";
import HelpKbArticle from "@/pages/HelpKbArticle";
import HelpKbSearch from "@/pages/HelpKbSearch";
import HelpTickets from "@/pages/HelpTickets";
import HelpTicketNew from "@/pages/HelpTicketNew";
import HelpTicketDetail from "@/pages/HelpTicketDetail";
import SuperAdminTickets from "@/pages/SuperAdminTickets";
import SuperAdminTicketDetail from "@/pages/SuperAdminTicketDetail";
import SuperAdminKb from "@/pages/SuperAdminKb";
import SuperAdminKbEdit from "@/pages/SuperAdminKbEdit";

// Phase B5 — Clear Test Data tool.
import DataTools from "@/pages/DataTools";
import SuperAdminDataCleanup from "@/pages/SuperAdminDataCleanup";

import KioskHome from "@/kiosk/KioskHome";
import KioskPair from "@/kiosk/KioskPair";
import KioskSignIn from "@/kiosk/KioskSignIn";
import KioskSignOut from "@/kiosk/KioskSignOut";
// Phase C1.5 — Module 7 Returning Visitor fast lane.
import KioskWelcomeBack from "@/kiosk/KioskWelcomeBack";
// Phase C1.4 — Replay mocked notifications tool.
import SuperAdminNotificationsReplay from "@/pages/SuperAdminNotificationsReplay";

function RoleLandingRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "host") return <Navigate to="/host/dashboard" replace />;
  // v1.2 PR-C — super_admins with no active org context land directly on
  // /super-admin instead of bouncing off org-scoped Dashboard endpoints
  // (which would flash a "no org" toast before settling).
  if (user.role === "super_admin" && !user.org_id) return <Navigate to="/super-admin" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LocaleProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <SwUpdater />
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/accept-invitation" element={<AcceptInvitationWrapper />} />
          <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
          {/* Phase 2 — Public visitor pass landing (no admin chrome) */}
          <Route path="/visitor-pass/:token" element={<VisitorPass />} />
          {/* Phase 3 — Public warden landing (token-gated, no admin chrome) */}
          <Route path="/evacuation/warden/:token" element={<EvacuationWarden />} />

          {/* Kiosk routes — no admin auth, full-screen */}
          <Route path="/kiosk" element={<KioskHome />} />
          <Route path="/kiosk/pair" element={<KioskPair />} />
          <Route path="/kiosk/sign-in" element={<KioskSignIn />} />
          <Route path="/kiosk/sign-out" element={<KioskSignOut />} />
          {/* Phase C1.5 — Module 7 Returning Visitor fast lane. */}
          <Route path="/kiosk/welcome-back" element={<KioskWelcomeBack />} />

          {/* Admin routes */}
          <Route path="/app" element={<ProtectedRoute><RoleLandingRedirect /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
          <Route path="/sites" element={<ProtectedRoute><Sites /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><OrgScopedRoute><Users /></OrgScopedRoute></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><OrgScopedRoute><Billing /></OrgScopedRoute></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><OrgScopedRoute><OrgSettings /></OrgScopedRoute></ProtectedRoute>} />
          <Route path="/settings/branding" element={<ProtectedRoute><OrgScopedRoute><SettingsBranding /></OrgScopedRoute></ProtectedRoute>} />
          <Route path="/settings/visitor-types" element={<ProtectedRoute><OrgScopedRoute><SettingsVisitorTypes /></OrgScopedRoute></ProtectedRoute>} />
          <Route path="/settings/consent-documents" element={<ProtectedRoute><OrgScopedRoute><SettingsConsentDocs /></OrgScopedRoute></ProtectedRoute>} />
          <Route path="/settings/badge-templates" element={<ProtectedRoute><OrgScopedRoute><SettingsBadgeTemplates /></OrgScopedRoute></ProtectedRoute>} />
          {/* v1.2 PR-C — consolidated Notifications tab (rules + templates). */}
          <Route path="/settings/notifications" element={<ProtectedRoute><OrgScopedRoute><SettingsNotifications /></OrgScopedRoute></ProtectedRoute>} />
          {/* Legacy aliases — preserve bookmarks, redirect with anchor fragments. */}
          <Route path="/settings/notification-rules" element={<Navigate to="/settings/notifications#rules" replace />} />
          <Route path="/settings/email-templates" element={<Navigate to="/settings/notifications#templates" replace />} />
          <Route path="/settings/integrations" element={<ProtectedRoute><OrgScopedRoute><SettingsIntegrations /></OrgScopedRoute></ProtectedRoute>} />
          {/* v2 PR-2B — Public API token management (org_admin gated server-side). */}
          <Route path="/settings/api-tokens" element={<ProtectedRoute><OrgScopedRoute><SettingsApiTokens /></OrgScopedRoute></ProtectedRoute>} />
          {/* Swagger UI for /api/v1/* is at backend `/api-docs` — no frontend route. */}
          <Route path="/audit" element={<ProtectedRoute><OrgScopedRoute><AuditLog /></OrgScopedRoute></ProtectedRoute>} />
          {/* Phase 2 — /notifications now serves the in-app feed (Module 6.3). */}
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/admin/notifications-log" element={<ProtectedRoute><OrgScopedRoute><NotificationsLog /></OrgScopedRoute></ProtectedRoute>} />
          <Route path="/super-admin/integrations" element={<ProtectedRoute><SuperAdminIntegrations /></ProtectedRoute>} />
          {/* Phase 2 — Pre-registrations */}
          <Route path="/pre-registrations" element={<ProtectedRoute><PreRegistrations /></ProtectedRoute>} />
          <Route path="/pre-registrations/new" element={<ProtectedRoute><PreRegistrationNew /></ProtectedRoute>} />
          {/* Phase 3 — Live dashboard, evacuation, reports */}
          <Route path="/dashboard/live" element={<ProtectedRoute><LiveDashboard /></ProtectedRoute>} />
          <Route path="/evacuation" element={<ProtectedRoute><Evacuation /></ProtectedRoute>} />
          <Route path="/evacuation/active" element={<ProtectedRoute><EvacuationActive /></ProtectedRoute>} />
          <Route path="/evacuation/history" element={<ProtectedRoute><EvacuationHistory /></ProtectedRoute>} />
          <Route path="/evacuation/history/:eventId" element={<ProtectedRoute><EvacuationHistoryDetail /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          {/* Phase 4 — Contractor workflow */}
          <Route path="/contractors" element={<ProtectedRoute><Contractors /></ProtectedRoute>} />
          <Route path="/contractors/:companyId" element={<ProtectedRoute><ContractorCompanyDetail /></ProtectedRoute>} />
          <Route path="/permits-to-work" element={<ProtectedRoute><PermitsToWork /></ProtectedRoute>} />
          <Route path="/admin/contractor-overrides" element={<ProtectedRoute><ContractorOverrides /></ProtectedRoute>} />
          {/* v1.2 PR-C — consolidated Contractor compliance tab (PPE + policy). */}
          <Route path="/settings/contractor-compliance" element={<ProtectedRoute><OrgScopedRoute><SettingsContractorCompliance /></OrgScopedRoute></ProtectedRoute>} />
          <Route path="/settings/ppe-requirements" element={<Navigate to="/settings/contractor-compliance#ppe" replace />} />
          <Route path="/settings/contractor-policy" element={<Navigate to="/settings/contractor-compliance#policy" replace />} />
          {/* Phase 4 — Watchlist */}
          <Route path="/settings/watchlist" element={<ProtectedRoute><OrgScopedRoute><SettingsWatchlist /></OrgScopedRoute></ProtectedRoute>} />
          <Route path="/admin/watchlist-hits" element={<ProtectedRoute><WatchlistHits /></ProtectedRoute>} />
          <Route path="/admin/watchlist-hits/:hitId" element={<ProtectedRoute><WatchlistHitDetail /></ProtectedRoute>} />
          {/* v1.2 PR-C — consolidated GDPR & privacy tab (retention + erasure). */}
          <Route path="/settings/gdpr-privacy" element={<ProtectedRoute><OrgScopedRoute><SettingsGdprPrivacy /></OrgScopedRoute></ProtectedRoute>} />
          <Route path="/settings/data-retention" element={<Navigate to="/settings/gdpr-privacy#retention" replace />} />
          <Route path="/admin/erasure-requests" element={<Navigate to="/settings/gdpr-privacy#erasure" replace />} />
          <Route path="/host/dashboard" element={<ProtectedRoute><HostMobileDashboard /></ProtectedRoute>} />
          <Route path="/reception/mobile" element={<ProtectedRoute><ReceptionMobile /></ProtectedRoute>} />
          <Route path="/super-admin" element={<ProtectedRoute><SuperAdmin /></ProtectedRoute>} />
          <Route path="/my-host" element={<ProtectedRoute><HostHome /></ProtectedRoute>} />

          {/* v1.2 — IA refactor (PR-A): account profile + kiosks fleet + super admin workspace tabs. */}
          <Route path="/kiosks" element={<ProtectedRoute><Kiosks /></ProtectedRoute>} />
          <Route path="/account/profile" element={<ProtectedRoute><AccountProfile /></ProtectedRoute>} />
          {/* Super admin workspace tabs (content shipped in PR-B). */}
          <Route path="/super-admin/overview" element={<ProtectedRoute><SuperAdminOverview /></ProtectedRoute>} />
          <Route path="/super-admin/provision" element={<ProtectedRoute><SuperAdminProvision /></ProtectedRoute>} />
          <Route path="/super-admin/audit-log" element={<ProtectedRoute><SuperAdminAuditLog /></ProtectedRoute>} />
          <Route path="/super-admin/privacy-acceptances" element={<ProtectedRoute><SuperAdminPrivacyAcceptances /></ProtectedRoute>} />
          <Route path="/super-admin/notifications-log" element={<ProtectedRoute><SuperAdminNotificationsLogCrossOrg /></ProtectedRoute>} />
          <Route path="/super-admin/maintenance" element={<ProtectedRoute><SuperAdminMaintenance /></ProtectedRoute>} />
          <Route path="/super-admin/build" element={<ProtectedRoute><SuperAdminBuild /></ProtectedRoute>} />

          {/* Phase B3 — Help (Knowledge Base + In-app Tickets). */}
          <Route path="/help/kb" element={<ProtectedRoute><HelpKb /></ProtectedRoute>} />
          <Route path="/help/kb/search" element={<ProtectedRoute><HelpKbSearch /></ProtectedRoute>} />
          <Route path="/help/kb/c/:slug" element={<ProtectedRoute><HelpKbCategory /></ProtectedRoute>} />
          <Route path="/help/kb/:slug" element={<ProtectedRoute><HelpKbArticle /></ProtectedRoute>} />
          <Route path="/help/tickets" element={<ProtectedRoute><HelpTickets /></ProtectedRoute>} />
          <Route path="/help/tickets/new" element={<ProtectedRoute><HelpTicketNew /></ProtectedRoute>} />
          <Route path="/help/tickets/:id" element={<ProtectedRoute><HelpTicketDetail /></ProtectedRoute>} />
          <Route path="/super-admin/tickets" element={<ProtectedRoute><SuperAdminTickets /></ProtectedRoute>} />
          <Route path="/super-admin/tickets/:id" element={<ProtectedRoute><SuperAdminTicketDetail /></ProtectedRoute>} />
          <Route path="/super-admin/kb" element={<ProtectedRoute><SuperAdminKb /></ProtectedRoute>} />
          <Route path="/super-admin/kb/new" element={<ProtectedRoute><SuperAdminKbEdit /></ProtectedRoute>} />
          <Route path="/super-admin/kb/:slug/edit" element={<ProtectedRoute><SuperAdminKbEdit /></ProtectedRoute>} />

          {/* Phase B5 — Clear Test Data tool. */}
          <Route path="/settings/data" element={<ProtectedRoute><OrgScopedRoute><DataTools /></OrgScopedRoute></ProtectedRoute>} />
          <Route path="/super-admin/data-cleanup" element={<ProtectedRoute><SuperAdminDataCleanup /></ProtectedRoute>} />

          {/* Phase C1.4 — Replay mocked notifications. */}
          <Route path="/super-admin/notifications-replay" element={<ProtectedRoute><SuperAdminNotificationsReplay /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
      </LocaleProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

function AcceptInvitationWrapper() {
  const url = new URL(window.location.href);
  const token = url.searchParams.get("token");
  if (token) return <Navigate to={`/accept-invitation/${token}`} replace />;
  return <Navigate to="/login" replace />;
}
